"use client"
import { ChevronDown, Menu, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import Academics from "@/components/common/academics";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { cn } from "@/lib/utils";

const SCROLL_Y_THRESHOLD = 80;
const MOBILE_VIEWPORT_WIDTH = 1210;

const primaryNav = [
    { label: "About", to: "/about" },
    // { label: "Academics", to: "/programs-static" },
    { label: "Admissions", to: "/admission/mode-of-payment" },
    { label: "Students", to: "/students", type: "anchor" },
    { label: "Insights", to: "/insight", type: "anchor" },
    { label: "Events", to: "/events", type: "anchor" },
    // { label: "Contact", to: "/contact", type: "anchor" },
    { label: "Enroll Now", to: "https://apply.patancollege.edu.np", redirect: true },
];

export default function Navbar() {
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [isAcadenicOpen, setIsAcadenicOpen] = useState(false);

    const headerRef = useRef<HTMLHeadElement>(null)

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const win_resize = () => {
            setIsDesktop(window.innerWidth >= MOBILE_VIEWPORT_WIDTH);
        };
        window.addEventListener("resize", win_resize);
        win_resize();
        return () => window.removeEventListener("resize", win_resize);
    }, []);

    const pathName = usePathname();

    useEffect(() => {
        if (isNavOpen) {
            setIsNavOpen(pre => !pre)
        }

    }, [pathName])


    const [belowHero, setBelowHero] = useState(false);
    // const [isVisible, setIsVisible] = useState(true);

    const shouldFullHeader = useMemo(() => {
        return isAcadenicOpen || !belowHero;
    }, [isAcadenicOpen, belowHero]);

    const isDark = useMemo(() => {
        return isAcadenicOpen || belowHero || pathName != "/" || isNavOpen
    }, [isAcadenicOpen, belowHero, pathName, isNavOpen]);
    /*  const isDark = useMemo(() => {
         return isAcadenicOpen || belowHero || isNavOpen
     }, [isAcadenicOpen, belowHero, isNavOpen]); */

    const handleX = () => {
        setIsAcadenicOpen(false);
        document.body.style.overflow = "";
    };

    useEffect(() => {
        const handleScroll = () => {
            setBelowHero(window.scrollY > SCROLL_Y_THRESHOLD);
            if (window.scrollY <= SCROLL_Y_THRESHOLD) {
                setIsNavOpen(false);
            }
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // If academics dropdown is opened, set overflow to hidden. If closed, restore. If a link is clicked, restore overflow as well.
    const handleAcademicsClick = () => {
        setIsAcadenicOpen((pre) => {
            const next = !pre;
            if (next) {
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.overflow = "";
            }
            return next;
        });
    };

    // Call this on any link in the academics dropdown menu
    const handleAcademicsLinkClick = () => {
        setIsAcadenicOpen(false);
        document.body.style.overflow = "";
    };


    return (
        <header
            datatype={isDark ? "dark" : "light"}
            ref={headerRef}
            style={{
                /* borderBottom: belowHero
                  ? "none"
                  : "0.0625rem solid rgba(255, 255, 255, 0.3)", */
                boxShadow: "0 0.3125rem 0.9375rem 0.0625rem rgba(0, 0, 0, 0.05)",
                transition: "height 0.35s ease",
            }}
            className={
                cn(
                    `fixed top-0 h-[3.75rem] md:h-[4.375rem] w-full max-w-[100vw] z-[100] shadow-[0_0.3125rem_0.9375rem_0.0625rem_rgba(0,0,0,0.05)]
                `,
                    `xl:transition-[height_0.35s_ease]`,
                    isDark ? "text-black" : "text-white",
                    {
                        'xl:h-[6.25rem] xl:border-b-[1px] xl:transition-[height_0.35s_ease]': isDesktop,
                        "xl:h-[4.375rem] xl:border-[#EDEDED] xl:h-[4.375rem]": !shouldFullHeader,
                        "xl:border-b-[rgba(255,255,255,0.3)] ": shouldFullHeader,
                    }
                )}
        >
            <div
                className={cn(
                    `relative w-full h-full max-w-[112.5rem] m-[0_auto] z-[2]`,
                    `
                    2xl:before:box-border 2xl:before:transition-[background-color_0.25s_ease] 
                    2xl:before:left-full 2xl:before:absolute 2xl:before:top-0 2xl:before:block 
                    2xl:before:w-full 2xl:before:h-full
                    2xl:after:box-border 2xl:after:transition-[background-color_0.25s_ease]
                    2xl:after:right-full 2xl:after:absolute 2xl:after:top-0 2xl:after:block 2xl:after:w-full
                    2xl:after:h-full
                    `,
                    {
                        '2xl:after:bg-white 2xl:before:bg-white': isDark,
                        '2xl:after:bg-transparent 2xl:before:bg-transparent': !isDark,
                        'xl:pr-[2.5rem] xl:z-[3]': isDesktop,
                    }

                )}
            >
                <Link
                    href="/"
                    onClick={() => {
                        setIsAcadenicOpen(false)
                    }}
                    className={cn(
                        // `border-r-[1px]`,
                        /* `xl:absolute xl:w-[10.625rem] xl:pt-[6.25rem] xl:bg-[length:6.25rem_auto] xl:bg-[position:2.5rem_1.4375rem] 
                        transition-[all_0.35s_ease]`, */
                        /*  `md:float-left md:top-auto md:left-auto md:w-[9.0625rem] md:pt-[4.375rem] md:bg-[length:4.6875rem_2.6875rem]
                         md:bg-[position:2.5rem_0.875rem]
                         `, */
                        `md:float-left md:top-auto md:left-auto md:w-[9.0625rem] md:pt-[4.375rem] md:bg-[size:5.25rem]
                        md:bg-center`,

                        `relative top-[0.625rem] left-[1.25rem] block float-left w-[4.375rem] h-0 pt-[2.5rem] 
                        overflow-hidden 
                        z-[101] opacity-[1] xl:bg-[length:7.25rem] xl:bg-[position:center]
                        transition-[background_0.25s_ease,border-color_0.25s_ease,opacity_0.25s_ease]
                        bg-no-repeat bg-center bg-[length:4.25rem]`,
                        // `bg-[url('/pcps.svg')]`,

                        `${isDark
                            ? "bg-[url('/pcps.svg')]"
                            : "bg-[url('/pcpslogowhite.png')]"
                        }`,

                        {
                            // 'xl:absolute xl:w-[10.625rem] xl:pt-[6.25rem] xl:bg-[length:6.25rem_auto] xl:bg-[position:2.5rem_1.4375rem] xl:transition-[all_0.35s_ease]': shouldFullHeader,
                            'xl:absolute xl:w-[10.625rem] xl:pt-[6.25rem] xl:bg-[length:9.25rem] xl:bg-[position:center] xl:transition-[all_0.35s_ease]': shouldFullHeader,
                            // 'xl:absolute xl:w-[10.625rem] xl:pt-[6.25rem] xl:bg-contain xl:bg-center xl:transition-[all_0.35s_ease]': shouldFullHeader,

                            // Full-height divider only below xl. At xl the short divider <span>s below are used instead.
                            'md:border-r md:border-r-[#EDEDED] xl:border-r-0': isDark || isAcadenicOpen,
                            'md:border-r md:border-r-[rgba(255,255,255,0.3)] xl:border-r-0': !isDark && !isAcadenicOpen,
                        }
                    )}
                />

                {/* University logo — official "in partnership with University of Bedfordshire" lockup, sits right after the PCPS logo */}
                <Link
                    href={"https://www.beds.ac.uk/"}
                    target="_blank"
                    aria-label="In partnership with University of Bedfordshire"
                    className={cn(
                        // Mobile / base: sits beside the PCPS logo since both are float-left. This is a wide horizontal
                        // wordmark logo, so it needs more width than a square mark.
                        `relative top-[1.125rem] left-[1.25rem] block float-left h-0 
                        w-[8.75rem] pt-[2.1875rem] overflow-hidden z-[101] opacity-[1]
                        bg-no-repeat bg-center bg-contain
                        pl-[0.9375rem] ml-[0.625rem]
                        transition-[background_0.25s_ease,border-color_0.25s_ease,opacity_0.25s_ease]
                        ${isDark
                            ? "bg-[url('/uob-partnership-color.png')]"
                            : "bg-[url('/uob-partnership-white.png')]"
                        }`,

                        // md breakpoint: match the taller compact header
                        `md:top-auto md:left-auto md:w-[9.375rem] md:pt-[2.5rem]`,

                        {
                            // Side borders below xl only. At xl they are removed (xl:border-x-0) and replaced by short dividers.
                            'border-x border-x-[#EDEDED] xl:border-x-0': isDark || isAcadenicOpen,
                            'border-x border-x-[rgba(255,255,255,0.3)] xl:border-x-0': !isDark && !isAcadenicOpen,

                            // Large logo mode (top of page, header not scrolled) — sits right after the big PCPS mark.
                            // Image is sized to the box width minus 2rem and centered, so it never touches the dividers.
                            'xl:absolute xl:top-0 xl:left-[10.625rem] xl:w-[10.625rem] xl:h-full xl:p-0 xl:ml-0 xl:bg-[length:calc(100%_-_2rem)_auto] xl:bg-[position:center] xl:transition-[all_0.35s_ease]': shouldFullHeader,
                            // Compact/scrolled header — smaller offset to match the smaller PCPS mark
                            'xl:absolute xl:top-0 xl:left-[9.0625rem] xl:w-[8.75rem] xl:h-full xl:p-0 xl:ml-0 xl:bg-[length:calc(100%_-_1.5rem)_auto] xl:bg-[position:center] xl:transition-[all_0.35s_ease]': !shouldFullHeader,
                        }
                    )}
                />

                {/* Short logo dividers (xl and up): centered vertically, never touch the header lines */}
                <span
                    aria-hidden="true"
                    className={cn(
                        "hidden xl:block absolute top-1/2 w-px -translate-y-1/2 z-[101] transition-all duration-300",
                        isDark || isAcadenicOpen ? "bg-[#EDEDED]" : "bg-[rgba(255,255,255,0.3)]",
                        shouldFullHeader ? "xl:left-[10.625rem] xl:h-12" : "xl:left-[9.0625rem] xl:h-8"
                    )}
                />
                <span
                    aria-hidden="true"
                    className={cn(
                        "hidden xl:block absolute top-1/2 w-px -translate-y-1/2 z-[101] transition-all duration-300",
                        isDark || isAcadenicOpen ? "bg-[#EDEDED]" : "bg-[rgba(255,255,255,0.3)]",
                        shouldFullHeader ? "xl:left-[21.25rem] xl:h-12" : "xl:left-[17.8125rem] xl:h-8"
                    )}
                />


                <nav
                    className={cn(
                        `md:top-[4.375rem] md:h-[calc(100vh-4.375rem)]`,

                        // xl:ml is set below (depends on header state) so the line under the links starts clear of the logo box
                        `xl:visible xl:relative xl:block xl:top-auto xl:min-h-0 xl:h-full xl:w-auto 
                        xl:max-w-none xl:transform-none xl:transition-none xl:left-0 
                        xl:bg-transparent xl:pointer-events-none xl:overflow-hidden xl:z-[100]`,

                        // `${isNavOpen ? "visible" : "invisible"}`,

                        `absolute invisible top-[3.75rem] left-0 w-full h-[calc(100vh-3.75rem)]
                         max-w-[90rem] overflow-auto bg-white z-[1] [transform:translateY(-100vh)]
                        transition-[transform_0.35s_ease-in,visibility_0.35s_ease-in]`,
                        {
                            // 'xl:visible xl:relative xl:block xl:top-auto xl:min-h-0 xl:h-full xl:w-auto xl:max-w-none xl:transition-none xl:left-0 xl:bg-transparent xl:pointer-events-none xl:overflow-hidden xl:ml-[10.625rem] xl:z-[100]': isDesktop,
                            'xl:ml-[22.5rem]': shouldFullHeader,   // full header: start 1.25rem after the logo box
                            'xl:ml-[18.75rem]': !shouldFullHeader, // scrolled header: unchanged
                            'xl:visible visible [transform:translateY(0)]': isNavOpen,
                            // 'xl:transform-none': isDesktop && !isNavOpen,

                        }
                    )}
                >
                    <span
                        className={cn(
                            `xl:h-full xl:pb-0`,
                            `relative flex flex-col min-h-full h-auto`,
                        )}
                    >
                        <ul
                            className={cn(
                                `xl:flex xl:flex-row xl:justify-end xl:h-[2.625rem] xl:w-full xl:p-[0_3.75rem_0_0] xl:pointer-events-auto`,
                                `md:p-[0_3.125rem]`,
                                `flex flex-col [list-style:none;] h-auto m-0 p-[0_1.25rem]`,
                                {
                                    'border-b border-b-[rgba(255,255,255,0.3)]': !isDark,
                                    'xl:border-b xl:border-b-[#EDEDED]': isDark,
                                    'xl:justify-start xl:items-center xl:h-full xl:w-[calc(100%_-_12.5rem)] xl:pr-0 xl:border-b-0 xl:opacity-0 xl:[translateY(-100%)] xl:[0.35s_ease_nav-collapse-anim] xl:transition-[opacity_0.25s_ease]': !shouldFullHeader,
                                    'xl:[transform:translateY(-100%)]': !shouldFullHeader && !isNavOpen,
                                    'xl:opacity-[1] xl:transition-[transform_0.25s_ease,opacity_0.25s_ease_0.1s] [transform:translateY(0)]': isNavOpen
                                }
                            )}
                        >
                            {/* acedemics mobile is missing */}
                            {primaryNav.map((item) => (
                                <li
                                    key={item.label}
                                    className={cn(
                                        `xl:p-0 xl:border-none xl:ml-[1.875rem]`,
                                        `md:p-0`,
                                        `border-t border-t-[#EDEDED] block flex-grow-0 m-0`,
                                        {
                                            'xl:m-[0_1.875rem_0_0] xl:transition-[margin_0s_linear_0.35s]': !shouldFullHeader
                                        }
                                    )}
                                >
                                    <Link
                                        href={item.to}
                                        style={{
                                            letterSpacing: "0.09375rem",
                                        }}
                                        onClick={() => {
                                            setIsAcadenicOpen(false)
                                        }}
                                        className={cn(
                                            `xl:text-[.75rem] xl:tracking-[0.09375rem] xl:p-[0.625rem_0] `,
                                            `bg-transparent border-none cursor-pointer inline-block text-[0.875rem] tracking-[0.09375rem] leading-[1.125rem] p-[1.75rem_0] [text-decoration:none] uppercase transition-[color_0.25s_ease] w-full`,
                                            `hover:underline`,
                                        )}
                                        target={item.redirect ? "_blank" : ""}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                            <li
                                // className="p-0 border-none ml-[1.875rem] block flex-grow-0 m-0 relative border-t-0"
                                className={cn(
                                    `p-0 border-none block flex-grow-0 m-0 relative border-t-0`,
                                    {
                                        'xl:ml-[1.875rem]': shouldFullHeader
                                    }
                                )}
                            /* className={cn(
                                `xl:p-0 xl:border-none xl:ml-[1.875rem]`,
                                `md:p-0`,
                                `border-t border-t-[#EDEDED] block flex-grow-0 m-0 orde`,
                                {
                                    'xl:m-[0_1.875rem_0_0] xl:transition-[margin_0s_linear_0.35s]': !shouldFullHeader
                                }
                            )} */
                            >
                                <button
                                    className={`pr-[1.25rem] uppercase xl:text-[0.75rem] text-[0.875rem] text-left w-full p-[0.625rem_0]
bg-transparent cursor-pointer inline-block transition-colors duration-200 hover:underline leading-[1.125rem]
after:absolute after:top-[-0.25rem] after:right-0 after:bottom-0 after:h-[1.25rem]
after:m-auto 
${isDark
                                            ? "after:content-[url('/svgs/caretdown.svg')]"
                                            : "after:content-[url('/svgs/caretdown-white.svg')]"
                                        }
after:scale-110 after:translate-x-[-0.1875rem] after:translate-y-[0.1875rem] after:leading-[1] 
border-t border-t-[#EDEDED] xl:border-0
                  `}
                                    style={{
                                        letterSpacing: "0.09375rem",
                                    }}
                                    onClick={handleAcademicsClick}
                                >
                                    courses
                                </button>
                            </li>
                        </ul>
                    </span>
                </nav>

                <div
                    className={cn(
                        `xl:absolute xl:border-b-0 xl:overflow-hidden xl:z-[50]`,
                        `relative top-0 left-0 h-full w-full z-[100] border-b border-b-[#EDEDED] transition-[background-color_0.25s_ease,border-color_0.25s_ease]`,
                        `md:after:table md:after:clear-both`,
                        {
                            'xl:border-b-0 xl:transition-[background-color_0.25s_ease,border-color_0.25s_ease] bg-transparent border-b border-b-[rgba(255,255,255,0.3)]': shouldFullHeader,
                            // 'xl:opacity-[0] xl:[transform:translateY(-100%)]': isNavOpen
                            'bg-white': isDark,
                            'bg-transparent': !isDark,
                        }
                    )}
                >

                    <div
                        className={cn(
                            // Base styles
                            `xl:absolute xl:w-[calc(100%_-_24.375rem)] xl:left-[10.625rem] 
                                xl:bottom-0 xl:h-[3.5625rem] xl:border-r-[#EDEDED] xl:[transform:translateY(0)] xl:transition-[height_0.35s_ease,border_0.35s_ease_0.1s]`,
                            `lg:flex`,
                            `md:flex-row md:justify-start md:items-center md:float-left md:h-full md:w-[calc(100%_-_17.8125rem)] md:p-[0_1.875rem]`,
                            // Show on desktop/larger screens, hide on mobile
                            `hidden xl:flex`,
                            {
                                // Use full white border in "full header" mode, transparent in compact
                                'xl:border-r-[rgba(255,255,255,0.3)]': shouldFullHeader,
                                'xl:border-r-transparent': !shouldFullHeader,
                                // Animate vertical slide out when nav is open (mobile/toggle scenario)
                                'xl:[transform:translateY(-100%)] xl:opacity-0': isNavOpen && !shouldFullHeader,
                                // Smaller left offset and height for compact header
                                'xl:h-full xl:left-[9.0625rem] xl:transition-[height_0.35s_ease,left_0.35s_ease,transform_0.25s_ease,opacity_0.25s_ease]': !shouldFullHeader
                            }
                        )}
                    >
                    </div>


                    <button
                        className={cn(
                            `border-l-[rgba(255,255,255,0.3)] bg-transparent 
                            transition-[background-color_0.25s_ease_0.35s]`,
                            `xl:hidden xl:w-[2.5rem] xl:top-0 xl:right-[8.125rem] xl:bottom-0`,
                            ` xl:m-auto border-l-0 xl:bg-transparent xl:opacity-0`,

                            `md:w-[4.375rem] md:pt-[4.3125rem] md:right-[4.375rem]`,

                            `[&:not(.noscroll)]:bg-transparent [&:not(.noscroll)]:transition-[background-color_0.25s_ease_0.35s]`,

                            `block h-0 leading-[3.6875rem] overflow-hidden p-[3.6875rem_0_0] 
                            relative w-[3.6875rem] bg-white border-none cursor-pointer absolute `,
                            // `  right-[3.75rem] transition-[opacity_0.25s_ease]`,
                            `right-0 transition-[opacity_0.25s_ease]`,
                            {
                                'xl:opacity-[1] xl:block xl:transition-[opacity_0.1s_ease]': !shouldFullHeader,
                                // 'h-[calc(100vh-4.375rem)]': !isDesktop
                            }
                        )}
                        aria-label="Toggle navigation"
                        type="button"
                        onClick={() => {
                            setIsNavOpen((pre) => !pre);
                        }}
                    >
                        <span
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                height: "100%",
                                width: "100%",
                                margin: "auto",
                                lineHeight: "59px",
                                textAlign: "center",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transform: "scale(0.9) translateY(0.125rem)",
                            }}
                        >
                            {isNavOpen ? (
                                <X className="size-6" strokeWidth={2} />
                            ) : (
                                <Menu className="size-6" strokeWidth={2} />
                            )}
                        </span>
                    </button>


                </div>
                {/* Search Button no need for now */}
                {/*    <button
            className="absolute bottom-0 p-0 right-[2.5rem] top-auto w-auto"
            style={{
              transition: "background-color 0s ease !important",
              fontWeight: 100,
              color: belowHero ? "#272727" : "#ffffff",
              fontSize: "0.875rem",
              height: "3.5625rem",
              letterSpacing: "0.0375rem",
              textTransform: "capitalize",
            }}
          >
            <Search
              className="size-4"
              style={{
                transition: "all 0.35s ease",
              }}A
            />
          </button> */}

            </div>
            <Academics
                isAcadenicOpen={isAcadenicOpen}
                setIsAcadenicOpen={setIsAcadenicOpen}
                handleX={handleX}
            />
        </header >
    );
}